import React from 'react';
import { Button, Modal, Form, InputGroup } from 'react-bootstrap';
import { updateTotalAmounts } from '../../util/data-store';
import LoadingLightbox from '../LoadingLightbox';
import {
  getFlowDataDirect,
  TickBox,
  emailValidationPattern,
} from '@adaptis/flow-components';
import Loader from '../Loader';
import { klaviyoTrack } from '../../KnoseAPI/Lib/Utils';
import { ISaveQuoteProps, ISaveQuoteModalProps } from './SaveQuote.types';

import './SaveQuote.scss';

export default function SaveQuote(props: ISaveQuoteProps) {
  const [modalShow, setModalShow] = React.useState(false);
  const [loadingLighboxShow, setLoadingLighboxShow] = React.useState(false);
  const quoteInfo = {
    petNames: getFlowDataDirect('totalAmounts.allPetNames'),
    petCount: getFlowDataDirect('petQty'),
    email: getFlowDataDirect('holderEmail'),
    phone: getFlowDataDirect('holderPhone'),
  };
  const quoteNo: string = getFlowDataDirect('quoteNo');

  async function saveQuoteHandler(e: any) {
    e.preventDefault();
    updateTotalAmounts();
    setModalShow(true);
  }

  async function modalSaveHandler(email: string, phone: string) {
    setModalShow(false);
    console.log(`Saved quote email: ${email}, phone: ${phone}`);
    setLoadingLighboxShow(true);

    // Log event with Klaviyo
    alert('TODO: push to Klaviyo');
    // if (!window.location.origin.includes('localhost')) {
    //   try {
    //     klaviyoTrack.quizAnswer({
    //       email: email,
    //       phone: phone,
    //     });
    //   } catch (error) {
    //     console.log('Klaviyo error: ', error);
    //   }
    // }
    // =======================

    await new Promise((resolve) => setTimeout(resolve, 1000)); // artificial 1s delay
    setLoadingLighboxShow(false);
  }

  function modalCloseHandler() {
    setModalShow(false);
  }
  return (
    <>
      {props.visible && quoteNo && (
        <div
          data-testid='SaveQuote'
          className={
            'flc_save-quote' + (props.className ? ' ' + props.className : '')
          }>
          <div>
            Quote No: <b>{quoteNo}</b>
          </div>
          <div>
            <a href='' onClick={saveQuoteHandler}>
              Save my quote
            </a>
          </div>

          <SaveQuoteModal
            show={modalShow}
            onClose={modalCloseHandler}
            onSave={modalSaveHandler}
            quoteNo={quoteNo}
            quoteInfo={quoteInfo}
          />
          <LoadingLightbox
            show={loadingLighboxShow}
            title='Sending your Quote...'
          />
        </div>
      )}
    </>
  );
}

function SaveQuoteModal(props: ISaveQuoteModalProps) {
  const [email, setEmail] = React.useState(props.quoteInfo.email);
  const [emailIsValid, setEmailIsValid] = React.useState(false);
  const [emailEnabled, setEmailEnabled] = React.useState(false);
  const emailInputRef = React.useRef<HTMLInputElement>(null);
  const [phone, setPhone] = React.useState(
    props.quoteInfo.phone ? props.quoteInfo.phone : '04'
  );
  const [phoneIsValid, setPhoneIsValid] = React.useState(false);
  const [phoneEnabled, setPhoneEnabled] = React.useState(false);
  const txtInputRef = React.useRef<HTMLInputElement>(null);
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = React.useState(false);

  React.useEffect(() => {
    validateEmail();
    validatePhone();
  }, []);

  React.useEffect(() => {
    validatePhone();
    validateSaveButton();
  }, [phone]);

  React.useEffect(() => {
    validateEmail();
    validateSaveButton();
  }, [email]);

  React.useEffect(() => {
    validateSaveButton();
  }, [emailEnabled, emailIsValid, phoneEnabled, phoneIsValid]);

  function validateEmail() {
    const emailRegEx = new RegExp(emailValidationPattern);
    const testEmailIsValid = emailRegEx.test(email);
    setEmailIsValid(testEmailIsValid);
  }

  function validatePhone() {
    const phoneRegex = /^04[0-9]{8}$/;
    const testPhoneIsValid = phoneRegex.test(phone);
    setPhoneIsValid(testPhoneIsValid);
  }

  function validateSaveButton() {
    if (phoneEnabled && !phoneIsValid) {
      setIsSaveButtonEnabled(false);
      return;
    }

    if (emailEnabled && !emailIsValid) {
      setIsSaveButtonEnabled(false);
      return;
    }

    if (!(phoneEnabled || emailEnabled)) {
      setIsSaveButtonEnabled(false);
      return;
    }

    setIsSaveButtonEnabled(true);
  }

  function handleEmailCheckChange(isChecked: boolean) {
    setEmailEnabled(isChecked);
    setTimeout(() => {
      if (isChecked && emailInputRef.current) {
        emailInputRef.current.focus();
        let length = emailInputRef.current.value.length;
        emailInputRef.current.setSelectionRange(length, length);
      }
    }, 20); // Need delay to make the focus work
  }

  function handleTxtCheckChange(isChecked: boolean) {
    setPhoneEnabled(isChecked);
    setTimeout(() => {
      if (isChecked && txtInputRef.current) {
        txtInputRef.current.focus();
        let length = txtInputRef.current.value.length;
        txtInputRef.current.setSelectionRange(length, length);
      }
    }, 20); // Need delay to make the focus work
  }

  const formConfig = [
    {
      id: 'emailBox',
      type: 'email',
      prompt: 'Your email address',
      maxLength: 50,
      required: true,
      dataField: 'holderEmail',
      invalidMessage: 'Please enter a valid email address',
      helpMessage: '',
    },
  ];

  return (
    <Modal
      className='save-quote-modal'
      show={props.show}
      onHide={props.onClose}
      aria-labelledby='contained-modal-title-vcenter'
      centered>
      <Modal.Header closeButton>
        <Modal.Title id='contained-modal-title-vcenter'>
          Save <em>{props.quoteInfo.petNames}'s</em> Quote
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {props.quoteNo === '' && <Loader />}
        {props.quoteNo != '' && (
          <>
            <div>
              Quote no.: <span className='quote-number'>{props.quoteNo}</span>
            </div>
            <div>Where would you like us to send your quote?</div>
            <TickBox
              className='mt-3'
              labelHTML=''
              stepSlug=''
              onChange={handleEmailCheckChange}>
              <div className='tick-label pt-1 me-2'>Email me</div>
              {emailEnabled && (
                <Form.Control
                  ref={emailInputRef}
                  className='me-2'
                  type='text'
                  placeholder='Email address'
                  disabled={!emailEnabled}
                  value={email}
                  isInvalid={!emailIsValid}
                  onChange={(e) => setEmail(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </TickBox>
            <TickBox
              className='mt-2'
              labelHTML=''
              stepSlug=''
              onChange={handleTxtCheckChange}>
              <div className='tick-label pt-1 me-2'>Text me</div>
              {phoneEnabled && (
                <Form.Control
                  ref={txtInputRef}
                  className='me-2'
                  type='text'
                  maxLength={10}
                  placeholder='Mobile number'
                  disabled={!phoneEnabled}
                  value={phone}
                  isInvalid={!phoneIsValid}
                  onKeyPress={(e) => {
                    if (
                      ![
                        '0',
                        '1',
                        '2',
                        '3',
                        '4',
                        '5',
                        '6',
                        '7',
                        '8',
                        '9',
                      ].includes(e.key)
                    ) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => setPhone(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </TickBox>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='outline-primary' onClick={props.onClose}>
          Close
        </Button>
        <Button
          variant='primary'
          onClick={() =>
            props.onSave(emailEnabled ? email : '', phoneEnabled ? phone : '')
          }
          disabled={!isSaveButtonEnabled}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
